import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Menu, X } from "lucide-react";

const LOGO_URL = "/logo-medsim-transparent.png";
const SIMULATOR_URL = process.env.REACT_APP_SIMULATOR_URL || "#";
const LANGS = ["ES", "EN", "DE"];

function LanguageSelector({ lang, setLang }) {
  return (
    <div className="flex items-center gap-1" data-testid="lang-selector">
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`lang-${l.toLowerCase()}`}
          onClick={() => setLang(l.toLowerCase())}
          className={`px-2.5 py-1 text-xs font-outfit tracking-widest uppercase rounded transition-all duration-200 ${
            lang === l.toLowerCase()
              ? "bg-[#1a6cf5] text-white"
              : "text-[#64748b] hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function MobileMenu({ lang, setLang, t }) {
  return (
    <div
      data-testid="mobile-menu"
      className="md:hidden bg-[#0a0f1f]/95 backdrop-blur-xl border-t border-[#2a3449]/50 px-6 py-6 space-y-4"
    >
      <Link to="/" className="block text-[#94a3b8] hover:text-white font-outfit">{t("nav.home")}</Link>
      <Link to="/contact" className="block text-[#94a3b8] hover:text-white font-outfit">{t("nav.contact")}</Link>
      <div className="flex gap-2 pt-2">
        <LanguageSelector lang={lang} setLang={setLang} />
      </div>
      <a
        href={SIMULATOR_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center bg-[#1a6cf5] text-white font-outfit px-6 py-3 rounded-full mt-4"
      >
        {t("nav.simulator")}
      </a>
    </div>
  );
}

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const onScroll = useCallback(() => setScrolled(window.scrollY > 20), []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a0f1f]/90 backdrop-blur-xl border-b border-[#2a3449]/50 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="MedSim" className="h-[60px] w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" data-testid="nav-home" className="text-sm font-outfit tracking-wide text-[#94a3b8] hover:text-white transition-colors">
            {t("nav.home")}
          </Link>
          <Link to="/contact" data-testid="nav-contact" className="text-sm font-outfit tracking-wide text-[#94a3b8] hover:text-white transition-colors">
            {t("nav.contact")}
          </Link>
          <div className="ml-4">
            <LanguageSelector lang={lang} setLang={setLang} />
          </div>
          <a
            href={SIMULATOR_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-simulator-btn"
            className="bg-[#1a6cf5] hover:bg-[#3b82f6] text-white text-sm font-outfit px-6 py-2.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
          >
            {t("nav.simulator")}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && <MobileMenu lang={lang} setLang={setLang} t={t} />}
    </nav>
  );
}
